import { useState } from 'react'
import {
  CloseButton,
  Button,
  NativeSelect,
  VStack,
  HStack,
  Text,
  Badge,
  Spinner,
  Field,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useContactLists } from '../../hooks/useContactList'
import { useMergeList } from '../../hooks/useTurfMap'

interface MergeListModalProps {
  isOpen: boolean
  onClose: () => void
  turfMapId: string
  campaignLabel: string
  onMerged: (turfMapId: string) => void
}

export function MergeListModal({
  isOpen,
  onClose,
  turfMapId,
  campaignLabel,
  onMerged,
}: MergeListModalProps) {
  const { data: allLists, isLoading: listsLoading } = useContactLists()
  const mergeMutation = useMergeList()

  const standaloneLists = allLists?.filter(l => !(l as any).campaignId)

  const [selectedListId, setSelectedListId] = useState('')

  const canSubmit = selectedListId !== '' && !mergeMutation.isPending

  const handleSubmit = async () => {
    if (!canSubmit) return

    try {
      const result = await mergeMutation.mutateAsync({
        turfMapId,
        contactListId: selectedListId,
      })

      toaster.create({
        title: 'Contacts merged',
        description: `Added ${result.addedCount} new contacts (${result.totalPoolSize} total in pool).`,
        type: 'success',
        duration: 5000,
      })

      handleClose()
      onMerged(result.turfMapId)
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message || 'Merge failed. Please try again.'
      toaster.create({
        title: 'Merge failed',
        description: message,
        type: 'error',
        duration: 7000,
      })
    }
  }

  const handleClose = () => {
    if (mergeMutation.isPending) return
    setSelectedListId('')
    onClose()
  }

  return (
    <Dialog.Root open={isOpen} size='md' closeOnInteractOutside={!mergeMutation.isPending} onOpenChange={e => {
      if (!e.open) {
        handleClose();
      }
    }}>
      <Portal>

        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>Add Contacts</Dialog.Header>
            {!mergeMutation.isPending && <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>}
            <Dialog.Body>
              {mergeMutation.isPending ? (
                <VStack py={8} gap={4}>
                  <Spinner size="lg" color="accent" />
                  <Text color="gray.600">Merging contacts...</Text>
                </VStack>
              ) : (
                <VStack gap={4} align="stretch">
                  <HStack bg="info.subtle" p={3} borderRadius="md">
                    <Text fontSize="sm" color="info.fg">Campaign:</Text>
                    <Badge bg="info.subtle" color="info.fg">{campaignLabel}</Badge>
                  </HStack>

                  <Field.Root required disabled={listsLoading}>
                    <Field.Label>Source Contact List</Field.Label>
                    <NativeSelect.Root size="sm">
                      <NativeSelect.Field
                        placeholder={listsLoading ? 'Loading lists...' : 'Select a contact list'}
                        value={selectedListId}
                        onChange={(e) => setSelectedListId(e.target.value)}>
                        {standaloneLists?.map(list => (
                          <option key={list.id} value={list.id}>
                            {list.label} ({list.memberCount?.toLocaleString() ?? '?'} members)
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Field.HelperText>
                      New contacts will be added to the campaign's turf pool. Duplicates are skipped.
                    </Field.HelperText>
                  </Field.Root>
                </VStack>
              )}
            </Dialog.Body>
            {!mergeMutation.isPending && (
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  colorPalette="brand"
                  onClick={handleSubmit}
                  disabled={!canSubmit}
                >
                  Merge Contacts
                </Button>
              </Dialog.Footer>
            )}
          </Dialog.Content>
        </Dialog.Positioner>

      </Portal>
    </Dialog.Root>
  );
}
